import { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const updateUserFromServer = async () => {
      if (!token) return setLoading(false);
      try {
        const res = await api.get('/auth.php?me');
        if (res.data.success && res.data.data) {
          const u = res.data.data;
          const userObj = {
            userId: u.user_id,
            fullName: u.full_name,
            email: u.email,
            roleId: u.role_id,
            roleName: u.role_name,
            avatarPath: u.avatar_path || null,
            avatarThumbPath: u.avatar_thumb_path || null
          };
          setUser(userObj);
          localStorage.setItem('user', JSON.stringify(userObj));
        } else {
          // fallback to stored user if any
          const storedUser = localStorage.getItem('user');
          if (storedUser) setUser(JSON.parse(storedUser));
        }
      } catch (err) {
        console.error('Failed to refresh auth user:', err);
        const storedUser = localStorage.getItem('user');
        if (storedUser) setUser(JSON.parse(storedUser));
      } finally {
        setLoading(false);
      }
    };

    updateUserFromServer();

    const onRefresh = () => {
      updateUserFromServer();
    };
    window.addEventListener('lit:auth-refresh', onRefresh);
    return () => window.removeEventListener('lit:auth-refresh', onRefresh);
  }, [token]);

  const login = async (email, password) => {
    try {
      const response = await api.post('/auth.php', { email, password });
      if (response.data.success) {
        // PHP backend wraps response in 'data' property
        const { token, user: apiUser } = response.data.data;
        // Transform API response to match frontend expectations
        const user = {
          userId: apiUser.user_id,
          fullName: apiUser.full_name,
          email: apiUser.email,
          roleId: apiUser.role_id,
          roleName: apiUser.role_name,
          avatarPath: apiUser.avatar_path || null,
          avatarThumbPath: apiUser.avatar_thumb_path || null
        };
        localStorage.setItem('token', token);
        localStorage.setItem('user', JSON.stringify(user));
        setToken(token);
        setUser(user);
        // Refresh canonical user from server (ensures avatar and other fields are current)
        try { const me = await api.get('/auth.php?me'); if (me.data.success && me.data.data) {
            const u = me.data.data; const userObj = { userId: u.user_id, fullName: u.full_name, email: u.email, roleId: u.role_id, roleName: u.role_name, avatarPath: u.avatar_path || null };
            setUser(userObj); localStorage.setItem('user', JSON.stringify(userObj)); }} catch(e){ /* ignore */ }
        return { success: true, user };
      }
      return { success: false, message: response.data.message, status: response.status || 200 };
    } catch (error) {
      console.error('Login failed:', error.response?.data || error.message);
      return { success: false, message: error.response?.data?.message || error.response?.data?.error || 'Login failed', status: error.response?.status };
    }
  };

  const navigate = useNavigate();
  const logout = () => {
    // Soft logout: clear local storage and in-memory user state but don't hard reload the whole page
    // (do NOT clear cookies here so any server-side sessions or 3rd-party cookies remain untouched)
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setToken(null);
    setUser(null);

    // Navigate to login using a soft navigation so the login form is shown without a full page reload
    navigate('/login', { replace: true });

    // Optionally, emit an event for other parts of the app to reset state if needed
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new Event('lit:logout'));
    }
  };

  const value = {
    user,
    token,
    loading,
    login,
    logout,
    isAuthenticated: !!token && !!user
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
